import React, { ReactElement, useEffect, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { IconButton, LinearProgress, TableCell, TableRow } from '@mui/material';
import API from 'api/API';
import {
  FileStatus,
  FileStatusMapping,
  FileUploadListResponse,
  FileUploadResponse,
  FilterStatus,
} from 'api/Responses/OrderResponses';
import { FileService, getFile, readJSONFiles } from 'services/FileService';
import { formatDate } from 'utils/helpers/DateTimeFormat.helpers';
import { getDownloadUrlFromGS } from 'utils/helpers/googleStorage.helpers';
import * as Styles from '../DeliveryPortalPage.styles';

interface FileUploadListItemProps {
  file: FileUploadListResponse;
  handleListDuplicates: (response: FileUploadResponse) => void;
}

const FileUploadListItem: React.FC<FileUploadListItemProps> = ({ file, handleListDuplicates }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [link, setLink] = useState<string>('');
  const fileStatus = Object.values(FilterStatus).find(filter => FileStatusMapping[filter]?.includes(file.status));

  const getFileData = async (uploadId: string): Promise<FileUploadResponse | null> => {
    try {
      const responseData = await API.orders.getFileUploadStatusV2(uploadId);

      if (responseData.status === FileStatus.DRAFT_COMPLETED) {
        const draftGSLocation = await getDownloadUrlFromGS(responseData.result.gs_location);
        const draftFile = await FileService.getFile(draftGSLocation, true, true);
        const resultData: FileUploadResponse = await readJSONFiles(draftFile, true);

        return resultData;
      }
    } catch (error) {
      API.errorHandler(error);
    }
    return null;
  };

  useEffect(() => {
    const getDownloadUrl = async () => {
      const downloadUrl = await getFile(file.gs_reference_uri, true, true);
      setLink(downloadUrl);
    };

    getDownloadUrl();
  }, [file]);

  const handleUpload = async () => {
    if (fileStatus === FilterStatus.ACTION_REQUIRED) {
      setLoading(true);
      const fileUpload = await getFileData(file.upload_id);
      fileUpload && handleListDuplicates(fileUpload);
      setLoading(false);
    }
  };

  const handleDownload = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const fileStatusIcon = (): ReactElement => {
    switch (fileStatus) {
      case FilterStatus.MERGED:
        return <CheckCircleOutlineIcon color="success" />;
      case FilterStatus.PENDING:
      case FilterStatus.ACTION_REQUIRED:
        return <PendingOutlinedIcon color="warning" />;
      case FilterStatus.DELETED:
      case FilterStatus.FAILED:
        return <ErrorOutlineOutlinedIcon color="error" />;
      default:
        return <DoNotDisturbOnOutlinedIcon color="disabled" />;
    }
  };

  return (
    <TableRow
      key={`${file.upload_id}`}
      onClick={handleUpload}
      style={fileStatus !== FilterStatus.ACTION_REQUIRED ? { background: '#ffffff' } : {}}
    >
      <TableCell>
        {loading ? (
          <LinearProgress />
        ) : (
          fileStatus && (
            <Styles.FileUploadListItemIconContainer>
              {fileStatusIcon()}
              {fileStatus}
            </Styles.FileUploadListItemIconContainer>
          )
        )}
      </TableCell>
      <TableCell>{file.filename}</TableCell>
      <TableCell>{file.created_by}</TableCell>
      <TableCell align="center">{formatDate(file.created, false)}</TableCell>
      <TableCell align="center">{file.total_kit_orders}</TableCell>
      <TableCell align="center" size="small">
        <IconButton onClick={e => handleDownload(e)} href={link} disabled={link ? false : true}>
          <GetAppIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default FileUploadListItem;
